import React, {FC, useEffect} from 'react'

import cn from 'classnames/bind'
import {observer} from 'mobx-react-lite'

import MobxCheckbox from 'components/elements/fields/MobxCheckbox/MobxCheckbox'
import MobxSelect from 'components/elements/fields/MobxSelect'
import MobxTextInput from 'components/elements/fields/MobxTextInput/MobxTextInput'
import SpinnerDots from 'components/elements/SpinnerDots'
import usStates from 'src/configs/usStates'
import {posthogTrack} from 'src/utils'
import {useW2Store} from 'src/utils/contexts/RootStore.context'

import s from './FormW2Invoice.module.scss'

const cx = cn.bind(s)

interface IFormW2InvoiceProps {
	className?: string
}

const FormW2Invoice: FC<IFormW2InvoiceProps> = ({className}) => {
	const w2Store = useW2Store()

	useEffect(() => {
		if (w2Store.isSecondStepFilled) {
			posthogTrack('[W2 FORM] Step 2 filled')
		}
	}, [w2Store.isSecondStepFilled])

	return (
		<table className={cx('invoice', className)}>
			<tbody>
				{w2Store.isCalculationLoading && (
					<tr data-testid="calculationLoading" className={cx('spinner')}>
						<td>
							<SpinnerDots className={cx('spinner__inner')} />
						</td>
					</tr>
				)}
				<tr>
					<td className={cx('cell', 'firstCol')}>
						<div className={cx('cell__content')}>
							<div className={cx('cell__marker')}>a</div>
							<MobxTextInput
								name="employeeSocialSecurityNumber"
								label="Employees Social Security Number"
								store={w2Store.form}
								wrapperClassName={cx('field')}
								className={cx('editable')}
							/>
						</div>
					</td>
					<td className={cx('cell')} colSpan={2}>
						<div className={cx('invoice__title')}>OMB No.1545-0008</div>
					</td>
				</tr>
				<tr>
					<td className={cx('cell')}>
						<div className={cx('cell__content')}>
							<div className={cx('cell__marker')}>b</div>
							<MobxTextInput
								name="employersIdentificationNumber"
								label="Employers Identification Number (EIN)"
								store={w2Store.form}
								wrapperClassName={cx('field')}
								className={cx('editable')}
							/>
						</div>
					</td>
					<td className={cx('cell', 'secondCol')}>
						<div className={cx('cell__content')}>
							<div className={cx('cell__marker')}>1</div>
							<MobxTextInput
								name="wagesTipsOther"
								type="text"
								isFormatText
								shouldHandleOldValue
								isNumber
								label="Wages,tips, other compensation"
								store={w2Store.form}
								wrapperClassName={cx('field', 'fixWidth')}
							/>
						</div>
					</td>
					<td className={cx('cell', 'thirdCol')}>
						<div className={cx('cell__content')}>
							<div className={cx('cell__marker')}>2</div>
							<MobxTextInput
								name="federalIncomeTax"
								type="text"
								isFormatText
								shouldHandleOldValue
								isNumber
								label="Federal Income tax withheld"
								required
								store={w2Store.form}
								wrapperClassName={cx('field', 'fixWidth')}
							/>
						</div>
					</td>
				</tr>
				<tr>
					<td className={cx('cell')} rowSpan={3}>
						<div className={cx('cell__content')}>
							<div className={cx('cell__marker')}>c</div>
							<div className={cx('cell__wrapper')}>
								<MobxTextInput
									name="companyName"
									placeholder="Company name"
									label="Employers Name, Address and Zip code"
									required
									store={w2Store.form}
									wrapperClassName={cx('field')}
									className={cx('editable')}
								/>
								<MobxTextInput
									name="companyAddress"
									placeholder="Address"
									store={w2Store.form}
									wrapperClassName={cx('field')}
									className={cx('editable')}
								/>
								<div className={cx('inputWrapper')}>
									<MobxTextInput
										name="companyCity"
										placeholder="City"
										store={w2Store.form}
										wrapperClassName={cx('field')}
										className={cx('editable')}
									/>
									<MobxSelect
										name="companyState"
										data-testid="companyState"
										placeholder="State"
										store={w2Store.form}
										options={usStates}
										wrapperClassName={cx('field', 'select')}
										className={cx('editable')}
									/>
									<MobxTextInput
										name="companyZip"
										placeholder="ZIP code"
										type="number"
										step="0.01"
										min="0"
										store={w2Store.form}
										wrapperClassName={cx('field')}
										className={cx('editable')}
									/>
								</div>
							</div>
						</div>
					</td>
					<td className={cx('cell')}>
						<div className={cx('cell__content')}>
							<div className={cx('cell__marker')}>3</div>
							<MobxTextInput
								name="socialSecurityWages"
								label="Social Security Wages"
								type="text"
								isFormatText
								shouldHandleOldValue
								isNumber
								store={w2Store.form}
								wrapperClassName={cx('field')}
							/>
						</div>
					</td>
					<td className={cx('cell')}>
						<div className={cx('cell__content')}>
							<div className={cx('cell__marker')}>4</div>
							<MobxTextInput
								name="socialSecurityTax"
								label="Social Security tax withheld"
								type="text"
								isFormatText
								shouldHandleOldValue
								isNumber
								required
								store={w2Store.form}
								wrapperClassName={cx('field', 'fixWidth')}
							/>
						</div>
					</td>
				</tr>
				<tr>
					<td className={cx('cell')}>
						<div className={cx('cell__content')}>
							<div className={cx('cell__marker')}>5</div>
							<MobxTextInput
								name="medicareWages"
								label="Medicare Wages and tips"
								type="text"
								isFormatText
								shouldHandleOldValue
								isNumber
								store={w2Store.form}
								wrapperClassName={cx('field')}
							/>
						</div>
					</td>
					<td className={cx('cell')}>
						<div className={cx('cell__content')}>
							<div className={cx('cell__marker')}>6</div>
							<MobxTextInput
								name="medicareTax"
								label="Medicare tax withheld"
								type="text"
								isFormatText
								shouldHandleOldValue
								isNumber
								required
								store={w2Store.form}
								wrapperClassName={cx('field', 'medicareTaxWithheldField')}
							/>
						</div>
					</td>
				</tr>
				<tr>
					<td className={cx('cell')}>
						<div className={cx('cell__content')}>
							<div className={cx('cell__marker')}>7</div>
							<MobxTextInput
								name="socialSecurityTips"
								label="Social Security tips"
								type="text"
								isFormatText
								shouldHandleOldValue
								isNumber
								store={w2Store.form}
								wrapperClassName={cx('field')}
							/>
						</div>
					</td>
					<td className={cx('cell')}>
						<div className={cx('cell__content')}>
							<div className={cx('cell__marker')}>8</div>
							<MobxTextInput
								name="allocatedTips"
								label="Allocated tips"
								type="text"
								isFormatText
								shouldHandleOldValue
								isNumber
								store={w2Store.form}
								wrapperClassName={cx('field')}
							/>
						</div>
					</td>
				</tr>
				<tr>
					<td className={cx('cell')}>
						<div className={cx('cell__content')}>
							<div className={cx('cell__marker')}>d</div>
							<p className={cx('cell__label')}>Control number</p>
						</div>
					</td>
					<td className={cx('cell')}>
						<div className={cx('cell__content')}>
							<div className={cx('cell__marker')}>9</div>
						</div>
					</td>
					<td className={cx('cell')}>
						<div className={cx('cell__content')}>
							<div className={cx('cell__marker')}>10</div>
							<p className={cx('cell__label')}>Dependent care benefits</p>
						</div>
					</td>
				</tr>
				<tr>
					<td className={cx('cell')} rowSpan={4}>
						<div className={cx('cell__content')}>
							<div className={cx('cell__marker')}>e</div>
							<div className={cx('cell__wrapper')}>
								<MobxTextInput
									name="employeeName"
									placeholder="Name"
									label="Employees Name, Address and Zip code"
									required
									store={w2Store.form}
									wrapperClassName={cx('field')}
									className={cx('editable')}
								/>
								<MobxTextInput
									name="employeeAddress"
									placeholder="Address"
									store={w2Store.form}
									wrapperClassName={cx('field')}
									className={cx('editable')}
								/>
								<div className={cx('inputWrapper')}>
									<MobxTextInput
										name="employeeCity"
										placeholder="City"
										store={w2Store.form}
										wrapperClassName={cx('field')}
										className={cx('editable')}
									/>
									<MobxSelect
										name="employeeState"
										data-testid="employeeState"
										placeholder="State"
										store={w2Store.form}
										options={usStates}
										wrapperClassName={cx('field', 'select')}
										className={cx('editable')}
									/>
									<MobxTextInput
										name="employeeZip"
										placeholder="ZIP code"
										type="number"
										step="0.01"
										min="0"
										store={w2Store.form}
										wrapperClassName={cx('field')}
										className={cx('editable')}
									/>
								</div>
							</div>
						</div>
					</td>
					<td className={cx('cell')}>
						<div className={cx('cell__content')}>
							<div className={cx('cell__marker')}>11</div>
							<p className={cx('cell__label')}>Nonqualified Plans</p>
						</div>
					</td>
					<td className={cx('cell')}>
						<div className={cx('cell__content')}>
							<div className={cx('cell__marker')}>12a</div>
							<MobxTextInput
								name="field12a"
								type="text"
								isFormatText
								shouldHandleOldValue
								isNumber
								store={w2Store.form}
								wrapperClassName={cx('field')}
							/>
						</div>
					</td>
				</tr>
				<tr>
					<td className={cx('cell')} rowSpan={2}>
						<div className={cx('cell__content')}>
							<div className={cx('cell__marker')}>13</div>
							<div className={cx('checkboxes')}>
								<MobxCheckbox
									store={w2Store.form}
									name="statutoryEmployee"
									label="Statutory employee"
									value={false}
									classNameLabel={cx('cell__checkbox')}
								/>
								<MobxCheckbox
									store={w2Store.form}
									name="retirementPlan"
									label="Retirement plan"
									value={false}
									classNameLabel={cx('cell__checkbox')}
								/>
								<MobxCheckbox
									store={w2Store.form}
									name="sickPay"
									label="Third-party sick pay"
									value={false}
									classNameLabel={cx('cell__checkbox')}
								/>
							</div>
						</div>
					</td>
					<td className={cx('cell')}>
						<div className={cx('cell__content')}>
							<div className={cx('cell__marker')}>12b</div>
							<MobxTextInput
								name="field12b"
								type="text"
								isFormatText
								shouldHandleOldValue
								isNumber
								store={w2Store.form}
								wrapperClassName={cx('field')}
							/>
						</div>
					</td>
				</tr>
				<tr>
					<td className={cx('cell')}>
						<div className={cx('cell__content')}>
							<div className={cx('cell__marker')}>12c</div>
							<MobxTextInput
								name="field12c"
								type="text"
								isFormatText
								shouldHandleOldValue
								isNumber
								store={w2Store.form}
								wrapperClassName={cx('field')}
							/>
						</div>
					</td>
				</tr>
				<tr>
					<td className={cx('cell')}>
						<div className={cx('cell__content')}>
							<div className={cx('cell__marker')}>14</div>
							<p className={cx('cell__label')}>Others</p>
						</div>
					</td>
					<td className={cx('cell')}>
						<div className={cx('cell__content')}>
							<div className={cx('cell__marker')}>12d</div>
							<MobxTextInput
								name="field12d"
								type="text"
								isFormatText
								shouldHandleOldValue
								isNumber
								store={w2Store.form}
								wrapperClassName={cx('field')}
							/>
						</div>
					</td>
				</tr>
				<tr>
					<td className={cx('cell', 'footer')} colSpan={3}>
						<div className={cx('footer__row')}>
							<div className={cx('footer__cell')}>
								<div className={cx('cell__marker')}>15</div>
								<MobxTextInput
									name="stateShort"
									required
									disabled
									store={w2Store}
									label="State"
									wrapperClassName={cx('footer__field', 'stateField', 'heightFix')}
								/>
							</div>
							<div className={cx('footer__cell')}>
								<MobxTextInput
									name="companyStateId"
									required
									store={w2Store.form}
									label="Employers State ID number"
									wrapperClassName={cx('footer__field', 'stateIdField', 'heightFix')}
								/>
							</div>
							<div className={cx('footer__cell')}>
								<div className={cx('cell__marker')}>16</div>
								<MobxTextInput
									name="stateWagesTips"
									label="State, Wages, tips etc"
									store={w2Store.form}
									type="text"
									isFormatText
									shouldHandleOldValue
									isNumber
									required
									wrapperClassName={cx('footer__field', 'heightFix')}
								/>
							</div>
							<div className={cx('footer__cell')}>
								<div className={cx('cell__marker')}>17</div>
								<MobxTextInput
									required
									name="stateIncomeTax"
									type="text"
									isFormatText
									shouldHandleOldValue
									isNumber
									store={w2Store.form}
									label="State income tax"
									wrapperClassName={cx('footer__field', 'stateIncomeField', 'heightFix')}
								/>
							</div>
							<div className={cx('footer__cell')}>
								<div className={cx('cell__marker')}>18</div>
								<MobxTextInput
									name="localWagesTips"
									store={w2Store.form}
									label="Local wages,tips etc"
									type="text"
									isFormatText
									shouldHandleOldValue
									isNumber
									wrapperClassName={cx('footer__field', 'heightFix')}
								/>
							</div>
							<div className={cx('footer__cell')}>
								<div className={cx('cell__marker')}>19</div>
								<MobxTextInput
									name="localIncomeTax"
									store={w2Store.form}
									type="text"
									isFormatText
									shouldHandleOldValue
									isNumber
									label="Local income tax"
									wrapperClassName={cx('footer__field', 'heightFix')}
								/>
							</div>
							<div className={cx('footer__cell')}>
								<div className={cx('cell__marker')}>20</div>
								<MobxTextInput
									name="localityName"
									store={w2Store.form}
									label="Locality name"
									wrapperClassName={cx('footer__field', 'no-indent', 'heightFix')}
								/>
							</div>
						</div>
					</td>
				</tr>
			</tbody>
		</table>
	)
}

FormW2Invoice.displayName = 'FormW2Invoice'

export default observer(FormW2Invoice)
